import { addToast, Button } from '@octano/global-ui';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Col, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../../components/ozmoit/common';
import useUserState from '../../../../../hooks/useUserState';
import { TranslationsKeys } from '../../../../../locales/translations';
import { PermissionName } from '../../../../../types/Auth';
import { downloadFromBlob } from '../../../../../utils/blob';
import StudentTabsHeader from '../../StudentTabsHeader';
import { exportSummaryAction } from '../actions';
import { useStudentLoaderData } from '../Loaders/StudentLoader';
import { StudentObservationsDataLoader } from '../Loaders/StudentObservationsLoader';
import StudentObservationCreateModal, {
  StudentObservationModalMethods,
} from './StudentObservationCreateModal';
import StudentObservationsFilters from './StudentObservationsFilters';
import StudentObservationsTable from './StudentObservationsTable';

const StudentObservations = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const { t } = useTranslation(TranslationsKeys.STUDENT_FILE_OBSERVATIONS);
  const { isAuthorizedTo } = useUserState();
  const data = useStudentLoaderData();
  const { result, query } = StudentObservationsDataLoader();

  const studentObservationModalRef = useRef<StudentObservationModalMethods>(
    null,
  );

  const hasPermission = useMemo(
    () =>
      isAuthorizedTo(
        [PermissionName.STUDENT_MANAGEMENT_STUDENT_FILE_MANAGE],
        true,
      ),
    [isAuthorizedTo],
  );

  const { action: handleExport, loading: exporting } = useActionKitImp(
    exportSummaryAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`list.export.success`),
        });
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`group.export.failed`),
          });
        },
      },
    },
  );

  const handlePressExport = useCallback(async () => {
    if (exporting) {
      return;
    }
    const response = await handleExport(query);
    if (!response.success) {
      return;
    }
    const blob = new Blob([response.data], {
      type: 'vnd.ms-excel',
    });
    downloadFromBlob(blob, `${t(`list.export.title`)}.xlsx`);
  }, [handleExport, query, exporting, t]);

  return (
    <>
      <StudentTabsHeader id={data.account.run} name={data.account.fullName} />

      <StudentObservationsFilters />

      <Row className="my-4">
        {hasPermission && (
          <Col xs="12" className="d-flex justify-content-end">
            <Button
              text={t('list.actions.downloadSummary')}
              className="ml-2"
              color="primary"
              type="button"
              icon="download"
              size="md"
              outlined
              loading={exporting}
              disabled={!result?.success || !result.data?.total}
              onClick={handlePressExport}
            />
            <Button
              text={t('list.actions.createObservation')}
              className="ml-2"
              color="primary"
              type="button"
              icon="plus"
              size="md"
              onClick={() => {
                studentObservationModalRef?.current?.open();
              }}
            />
          </Col>
        )}
      </Row>

      <Row>
        <Col xs={12}>
          <StudentObservationsTable refModal={studentObservationModalRef} />
        </Col>
      </Row>

      <StudentObservationCreateModal
        studentId={studentId}
        ref={studentObservationModalRef}
      />
    </>
  );
};

export default StudentObservations;
